import { User } from '../types/auth';

const TOKEN_KEY = 'foundation_token';

/**
 * Store JWT token in localStorage
 */
export const setToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
};

/**
 * Get JWT token from localStorage
 */
export const getToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Remove JWT token from localStorage
 */
export const removeToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
};

/**
 * Parse JWT token to extract user information
 */
export const parseToken = (token: string): User | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    
    return {
      email: payload.email || '',
      displayName: payload.name || payload.displayName,
      roles: payload.role ? (Array.isArray(payload.role) ? payload.role : [payload.role]) : [],
      isGoogleAccount: false, // This would need to be included in the token if needed
      isMicrosoftAccount: false, // This would need to be included in the token if needed
      createdUtc: new Date().toISOString() // This would need to be included in the token if needed
    };
  } catch (error) {
    console.error('Error parsing token:', error);
    return null;
  }
};

/**
 * Check if token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (error) {
    return true;
  }
};

/**
 * Get remaining seconds until token expires.
 * Returns 0 if token is already expired or invalid.
 */
export const getTokenExpirationSeconds = (token: string): number => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const remaining = payload.exp - Date.now() / 1000;
    return remaining > 0 ? Math.floor(remaining) : 0;
  } catch {
    return 0;
  }
};

/**
 * Get current user from stored token
 */
export const getCurrentUser = (): User | null => {
  const token = getToken();
  if (!token || isTokenExpired(token)) {
    removeToken();
    return null;
  }
  return parseToken(token);
};

/**
 * Check if user has a specific role
 */
export const hasRole = (user: User | null, role: string): boolean => {
  return user?.roles.includes(role) ?? false;
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  const token = getToken();
  return token !== null && !isTokenExpired(token);
};
